import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { z } from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isSuperAdmin } from "@/lib/permissions";
import { useConfigModules } from "@/features/configuration/api";
import {
  ApprovalRulesTab, MasterDataTab, NotificationRulesTab,
  SlaRulesTab, StatusesTab, WorkflowsTab,
} from "@/features/configuration/tabs";
import { CodeGenerationTab } from "@/features/configuration/CodeGenerationTab";
import { AuditLogTab } from "@/features/configuration/AuditLogTab";
import { ProcurementMethodsTab } from "@/features/configuration/ProcurementMethodsTab";
import { DocumentSettingsTab } from "@/features/configuration/DocumentSettingsTab";
import { ApprovalWorkflowConfig, WORKFLOW_BY_MODULE } from "@/features/configuration/ApprovalWorkflowConfig";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Layers, Globe, Database, Tag, Workflow, ShieldCheck,
  Clock, Bell, FileCode, History,
} from "lucide-react";

const configSearchSchema = z.object({
  section: z.enum(["module", "global"]).optional(),
  tab: z.string().optional(),
  module: z.string().optional(),
});

type ConfigSearch = z.infer<typeof configSearchSchema>;

export const Route = createFileRoute("/_authenticated/configuration")({
  validateSearch: (search: Record<string, unknown>): ConfigSearch => {
    return {
      section: search.section === "global" ? "global" : (search.section === "module" ? "module" : undefined),
      tab: typeof search.tab === "string" ? search.tab : undefined,
      module: typeof search.module === "string" ? search.module : undefined,
    };
  },
  component: () => (
    <RouteGuard module="configuration"><ConfigurationCentre /></RouteGuard>
  ),
});

function ConfigurationCentre() {
  const { data: me } = useCurrentUser();
  const readOnly = !isSuperAdmin(me?.roles ?? []);
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/configuration" });
  const { data: modules, isLoading: modulesLoading, error: modulesError } = useConfigModules();

  const [section, setSection] = useState<"module" | "global">(search.section ?? "module");
  const [moduleKey, setModuleKey] = useState<string>(search.module ?? "");
  const [moduleTab, setModuleTab] = useState<string>(
    search.section !== "global" && search.tab ? search.tab : "master-data"
  );
  const [globalTab, setGlobalTab] = useState<string>(
    search.section === "global" && search.tab ? search.tab : "code-generation"
  );

  // Sync state if URL search params change externally
  useEffect(() => {
    if (search.section && search.section !== section) {
      setSection(search.section);
    }
  }, [search.section]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (search.module && search.module !== moduleKey) {
      setModuleKey(search.module);
    }
  }, [search.module]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (search.tab) {
      if (section === "global" && search.tab !== globalTab) {
        setGlobalTab(search.tab);
      } else if (section === "module" && search.tab !== moduleTab) {
        setModuleTab(search.tab);
      }
    }
  }, [search.tab, section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default selected module: work_orders if present, else first active.
  useEffect(() => {
    if (!moduleKey && modules && modules.length > 0) {
      const preferred = modules.find((m) => m.module_key === "work_orders");
      const defaultKey = (preferred ?? modules[0]).module_key;
      setModuleKey(defaultKey);
      if (!search.module && section === "module") {
        navigate({
          to: "/configuration",
          search: { section, tab: moduleTab, module: defaultKey },
          replace: true,
        });
      }
    }
  }, [moduleKey, modules, search.module, section, moduleTab, navigate]);

  // If currently on SLA tab but the selected module is not tickets, fallback to master-data
  useEffect(() => {
    if (moduleKey && ((moduleKey !== "tickets" && moduleTab === "sla") || (moduleKey !== "purchase_orders" && moduleTab === "procurement-methods") || (!["purchase_indent", "indents", "purchase_orders"].includes(moduleKey) && moduleTab === "document-settings"))) {
      setModuleTab("master-data");
      if (section === "module") {
        navigate({
          to: "/configuration",
          search: { section: "module", tab: "master-data", module: moduleKey },
          replace: true,
        });
      }
    }
  }, [moduleKey, moduleTab, section, navigate]);

  const handleSectionChange = (newSection: "module" | "global") => {
    setSection(newSection);
    const activeTab = newSection === "global" ? globalTab : moduleTab;
    navigate({
      to: "/configuration",
      search: {
        section: newSection,
        tab: activeTab,
        module: newSection === "module" ? moduleKey : undefined,
      },
      replace: true,
    });
  };

  const handleModuleChange = (newModuleKey: string) => {
    setModuleKey(newModuleKey);
    navigate({
      to: "/configuration",
      search: {
        section: "module",
        tab: (moduleTab === "sla" && newModuleKey !== "tickets") || (moduleTab === "procurement-methods" && newModuleKey !== "purchase_orders") || (moduleTab === "document-settings" && !["purchase_indent", "indents", "purchase_orders"].includes(newModuleKey)) ? "master-data" : moduleTab,
        module: newModuleKey,
      },
      replace: true,
    });
  };

  const handleModuleTabChange = (newTab: string) => {
    setModuleTab(newTab);
    navigate({
      to: "/configuration",
      search: {
        section: "module",
        tab: newTab,
        module: moduleKey,
      },
      replace: true,
    });
  };

  const handleGlobalTabChange = (newTab: string) => {
    setGlobalTab(newTab);
    navigate({
      to: "/configuration",
      search: {
        section: "global",
        tab: newTab,
      },
      replace: true,
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-8 pb-16">
      {/* Top Header & Section Switcher */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b pb-5">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Configuration Centre</h1>
          <p className="text-sm text-muted-foreground">
            Manage global system settings and module-specific workflows, master data, and rules.
          </p>
        </div>

        {/* Section Toggle Pill */}
        <div className="inline-flex items-center rounded-lg border bg-muted/60 p-1 text-muted-foreground shadow-sm self-start md:self-auto">
          <button
            type="button"
            onClick={() => handleSectionChange("module")}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${
              section === "module"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Layers className="h-4 w-4" />
            <span>Module Configurations</span>
          </button>
          <button
            type="button"
            onClick={() => handleSectionChange("global")}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${
              section === "global"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Globe className="h-4 w-4" />
            <span>Global Settings</span>
          </button>
        </div>
      </div>

      {readOnly && (
        <Alert>
          <AlertDescription>You have read-only access. Only Super Admin can modify configuration.</AlertDescription>
        </Alert>
      )}

      {/* SECTION 1: MODULE CONFIGURATIONS */}
      {section === "module" && (
        <div className="space-y-6">
          {/* Module Selector Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active Module</Label>
                <p className="text-sm text-muted-foreground">
                  Select a module to manage its master data, statuses, workflows, and rules.
                </p>
              </div>
            </div>

            <div className="w-full sm:w-72">
              <Select value={moduleKey} onValueChange={handleModuleChange} disabled={modulesLoading || !modules?.length}>
                <SelectTrigger className="w-full font-medium">
                  <SelectValue placeholder={modulesLoading ? "Loading modules..." : "Select module"} />
                </SelectTrigger>
                <SelectContent>
                  {(modules ?? []).map((m) => (
                    <SelectItem key={m.module_key} value={m.module_key} className="cursor-pointer">
                      {m.module_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {modulesError && (
                <p className="mt-1.5 text-xs text-destructive">Unable to load modules.</p>
              )}
              {!modulesLoading && !modulesError && modules && modules.length === 0 && (
                <p className="mt-1.5 text-xs text-muted-foreground">No modules found.</p>
              )}
            </div>
          </div>

          {/* Module Horizontal Tabs (Matching Admin Module Style) */}
          <Tabs value={moduleTab} onValueChange={handleModuleTabChange} className="space-y-4">
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="master-data" className="gap-1.5">
                <Database className="h-3.5 w-3.5" />
                <span>Master Data</span>
              </TabsTrigger>
              <TabsTrigger value="statuses" className="gap-1.5">
                <Tag className="h-3.5 w-3.5" />
                <span>Statuses</span>
              </TabsTrigger>
              <TabsTrigger value="workflows" className="gap-1.5">
                <Workflow className="h-3.5 w-3.5" />
                <span>Workflows</span>
              </TabsTrigger>
              <TabsTrigger value="approvals" className="gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>Approval Rules</span>
              </TabsTrigger>
              {(moduleKey === "purchase_indent" || moduleKey === "indents" || moduleKey === "purchase_orders") && (
                <TabsTrigger value="document-settings" className="gap-1.5">
                  <FileCode className="h-3.5 w-3.5" />
                  <span>Document Settings</span>
                </TabsTrigger>
              )}
              {moduleKey === "purchase_orders" && (
                <TabsTrigger value="procurement-methods" className="gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span>Procurement Methods</span>
                </TabsTrigger>
              )}
              {moduleKey === "tickets" && (
                <TabsTrigger value="sla" className="gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  <span>SLA Rules</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="notifications" className="gap-1.5">
                <Bell className="h-3.5 w-3.5" />
                <span>Notification Rules</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="master-data" className="w-full pt-2">
              <MasterDataTab moduleKey={moduleKey} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="statuses" className="w-full pt-2">
              <StatusesTab moduleKey={moduleKey} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="workflows" className="w-full pt-2">
              <WorkflowsTab moduleKey={moduleKey} readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="approvals" className="w-full pt-2">
              {WORKFLOW_BY_MODULE[moduleKey]
                ? <ApprovalWorkflowConfig moduleKey={moduleKey} readOnly={readOnly} />
                : <ApprovalRulesTab moduleKey={moduleKey} readOnly={readOnly} />}
            </TabsContent>
            {(moduleKey === "purchase_indent" || moduleKey === "indents" || moduleKey === "purchase_orders") && (
              <TabsContent value="document-settings" className="w-full pt-2">
                <DocumentSettingsTab kind={moduleKey === "purchase_orders" ? "po" : "pi"} readOnly={readOnly} />
              </TabsContent>
            )}
            {moduleKey === "purchase_orders" && (
              <TabsContent value="procurement-methods" className="w-full pt-2">
                <ProcurementMethodsTab readOnly={readOnly} />
              </TabsContent>
            )}
            {moduleKey === "tickets" && (
              <TabsContent value="sla" className="w-full pt-2">
                <SlaRulesTab moduleKey={moduleKey} readOnly={readOnly} />
              </TabsContent>
            )}
            <TabsContent value="notifications" className="w-full pt-2">
              <NotificationRulesTab moduleKey={moduleKey} readOnly={readOnly} />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* SECTION 2: GLOBAL SETTINGS */}
      {section === "global" && (
        <div className="space-y-6">
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Global System Configurations</h3>
                <p className="text-sm text-muted-foreground">
                  System-wide settings and logs that govern asset tagging and configuration audit history across all modules.
                </p>
              </div>
            </div>
          </div>

          {/* Global Horizontal Tabs (Matching Admin Module Style) */}
          <Tabs value={globalTab} onValueChange={handleGlobalTabChange} className="space-y-4">
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="code-generation" className="gap-1.5">
                <FileCode className="h-3.5 w-3.5" />
                <span>Code Generation</span>
              </TabsTrigger>
              <TabsTrigger value="audit" className="gap-1.5">
                <History className="h-3.5 w-3.5" />
                <span>Audit Log</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="code-generation" className="w-full pt-2">
              <CodeGenerationTab readOnly={readOnly} />
            </TabsContent>
            <TabsContent value="audit" className="w-full pt-2">
              <AuditLogTab />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
